import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface WalletTransaction {
  id: number;
  transaction_type: string;
  amount: string;
  balance_before: string;
  balance_after: string;
  description: string;
  status: string;
  created_at: string;
  transaction_id: string;
}

interface WalletCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WalletCreditsModal: React.FC<WalletCreditsModalProps> = ({ isOpen, onClose }) => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', handler);
    setIsNarrow(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCreditTransactions();
    }
  }, [isOpen]);

  const fetchCreditTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch credit transactions from API
      const response = await api.get('/merchants/my-wallet/transactions?type=credit&limit=10');
      const data = response.data.data;
      
      setWalletBalance(data.current_balance || 0);
      setTransactions(data.transactions || []);
      setTotalCredits(data.statistics?.total_credits || 0);
      setTotalCreditAmount(data.statistics?.total_credit_amount || 0);
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(numValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 m-0">💰 Wallet Credit History</h2>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer text-xl" onClick={onClose}>✕</button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div
            className="grid grid-cols-1 gap-4 mb-6"
            style={!isNarrow ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined}
          >
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3 min-w-0">
              <span className="text-2xl">💳</span>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase m-0 mb-0.5">Current Wallet Balance</h4>
                <p className="text-lg font-bold text-slate-800 m-0">{formatCurrency(walletBalance)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3 min-w-0">
              <span className="text-2xl">⬆️</span>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase m-0 mb-0.5">Total Credits</h4>
                <p className="text-lg font-bold text-slate-800 m-0">{totalCredits} transactions</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3 min-w-0">
              <span className="text-2xl">💵</span>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase m-0 mb-0.5">Total Credit Amount</h4>
                <p className="text-lg font-bold text-slate-800 m-0">{formatCurrency(totalCreditAmount)}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-violet-600">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-4" />
              <p className="text-sm m-0">Loading credit history...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <span className="text-5xl mb-4">📭</span>
              <h3 className="text-lg font-bold text-slate-800 m-0 mb-2">No Credit Transactions Yet</h3>
              <p className="text-sm text-slate-600 m-0 mb-1">Your wallet credit history will appear here.</p>
              <p className="text-sm text-slate-500 m-0">Contact your manager to add money to your wallet.</p>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider m-0 mb-4">Recent Credit Transactions (Last 10)</h3>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full min-w-[700px] text-sm text-left">
                  <thead className="bg-slate-100 border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4 font-semibold text-slate-700">Date</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Description</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Amount</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Balance Before</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Balance After</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Transaction ID</th>
                      <th className="py-3 px-4 font-semibold text-slate-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {transactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-slate-50">
                        <td className="py-3 px-4 text-slate-800">{formatDate(txn.created_at)}</td>
                        <td className="py-3 px-4 text-slate-800">{txn.description}</td>
                        <td className="py-3 px-4 font-semibold text-emerald-600">+{formatCurrency(parseFloat(txn.amount))}</td>
                        <td className="py-3 px-4 text-slate-800">{formatCurrency(parseFloat(txn.balance_before || '0'))}</td>
                        <td className="py-3 px-4 text-slate-800">{formatCurrency(parseFloat(txn.balance_after || '0'))}</td>
                        <td className="py-3 px-4 font-mono text-slate-800 text-xs">{txn.transaction_id}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block py-1 px-2 rounded-full text-xs font-semibold ${txn.status?.toLowerCase() === 'success' || txn.status?.toLowerCase() === 'completed' ? 'bg-emerald-100 text-emerald-700' : txn.status?.toLowerCase() === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {txn.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex flex-wrap gap-3 justify-end" style={{ flexShrink: 0 }}>
          <button type="button" onClick={onClose} className="py-2.5 px-5 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm cursor-pointer hover:bg-slate-50 whitespace-nowrap shrink-0">Close</button>
          <button type="button" onClick={() => window.location.href = '/merchant/reports'} className="py-2.5 px-5 rounded-xl bg-violet-600 text-white font-semibold text-sm cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">View All Transactions →</button>
        </div>
      </div>
    </div>
  );
};

export default WalletCreditsModal;










